import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import Images from '../Images';

const CoreServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const services = [
    {
      id: 1,
      title: "Bulk SMS & Bulk Voice Broadcast",
      description: "Fast and reliable SMS delivery with reports, discounts, and 24/7 support.",
      image: Images.bulk1,
      imageBg: "bg-[#1A3A8F]",
    },
    {
      id: 2,
      title: "Reliable Online Voting Services",
      description: "Fast and reliable SMS delivery with reports, discounts, and 24/7 support.",
      image: Images.woman,
      imageBg: "bg-brand-orange",
    },
    {
      id: 3,
      title: "USSD Services SMS",
      description: "Fast and reliable SMS delivery with reports, discounts, and 24/7 support.",
      image: Images.ussd,
      imageBg: "bg-[#1A3A8F]",
    },
    {
      id: 4,
      title: "Bulk Email",
      description: "Fast and reliable SMS delivery with reports, discounts, and 24/7 support.",
      image: Images.email1,
      imageBg: "bg-brand-orange",
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 60,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <section id="services" className="py-16 sm:py-20 lg:py-28 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-[86rem] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            Core Services
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed px-4">
            Everything you need to effectively communicate, inform, and engage your customers
            at scale is right here, ensuring that your outreach is both impactful and meaningful.
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
        >
          {services.map((service) => (
            <ServiceCard key={service.id} service={service} variants={cardVariants} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const ServiceCard = ({ service, variants }) => {
  return (
    <motion.div
      variants={variants}
      whileHover={{ 
        y: -8,
        transition: { duration: 0.3 }
      }}
      className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 w-full max-w-sm mx-auto sm:max-w-none flex flex-col"
    >
      {/* Card Image Section with brand bg */}
      <div className={`relative w-full h-56 sm:h-60 lg:h-56 ${service.imageBg} flex items-center justify-center overflow-hidden`}>
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-contain object-center p-4 transition-transform duration-700 group-hover:scale-105"
        />
      </div>

      {/* Card Content Section */}
      <div className="p-5 sm:p-6 bg-white flex flex-col flex-1">
        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3 group-hover:text-[#FF6B2C] transition-colors duration-300 line-clamp-2">
          {service.title}
        </h3>
        <p className="text-sm sm:text-base text-gray-600 leading-relaxed line-clamp-3">
          {service.description}
        </p>
      </div>
    </motion.div>
  );
};

export default CoreServicesSection;